import { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { ROUTES } from '@constants'
import headerImage from '@assets/images/header_accueil.png'
import groupImage from '@assets/images/group-afro-americans-working-together.png'
import telImage from '@assets/images/Frame.png'

// Hook pour détecter quand un élément entre dans le viewport
function useInView(threshold = 0.15) {
  const ref = useRef(null)
  const [inView, setInView] = useState(false)
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setInView(true); observer.disconnect() } },
      { threshold }
    )
    if (ref.current) observer.observe(ref.current)
    return () => observer.disconnect()
  }, [threshold])
  return [ref, inView]
}

const WORDS = ['annonces', 'produits', 'vidéos', 'bannières']

const HomePage = () => {
  const [currentSlide, setCurrentSlide] = useState(0)
  const [displayed, setDisplayed]       = useState('')
  const [wordIndex, setWordIndex]        = useState(0)
  const [isDeleting, setIsDeleting]      = useState(false)
  
  // Tous les témoignages individuels
  
  // Refs pour les animations scroll
  const [refAbout,    inAbout]    = useInView()
  const [refFeatures, inFeatures] = useInView()
  const [refTestim,   inTestim]   = useInView()
  const [refSteps,    inSteps]    = useInView()
  const [refCta,      inCta]      = useInView()
  const [refHeroText, inHeroText] = useInView(0.01)
  const [refHeroBlock, inHeroBlock] = useInView(0.01)

  const allTestimonials = [
    { id: 1, name: 'Albert Yahout', company: 'Tech Solutions', image: 'https://i.pravatar.cc/150?img=1' },
    { id: 2, name: 'Marie Kouadio', company: 'Marketing Pro', image: 'https://i.pravatar.cc/150?img=2' },
    { id: 3, name: 'Jean Koffi', company: 'Design Studio', image: 'https://i.pravatar.cc/150?img=3' },
    { id: 4, name: 'Sarah N\'Guessan', company: 'Business Corp', image: 'https://i.pravatar.cc/150?img=4' },
    { id: 5, name: 'Paul Traoré', company: 'Innovation Labs', image: 'https://i.pravatar.cc/150?img=5' },
    { id: 6, name: 'Fatou Diallo', company: 'Creative Agency', image: 'https://i.pravatar.cc/150?img=6' },
    { id: 7, name: 'Amadou Koné', company: 'StartUp Inc', image: 'https://i.pravatar.cc/150?img=7' },
    { id: 8, name: 'Aïcha Bamba', company: 'Digital Media', image: 'https://i.pravatar.cc/150?img=8' },
    { id: 9, name: 'Moussa Sanogo', company: 'Web Services', image: 'https://i.pravatar.cc/150?img=9' },
  ]

  // Grouper par 3 pour desktop
  const testimonialsDesktop = [
    allTestimonials.slice(0, 3),
    allTestimonials.slice(3, 6),
    allTestimonials.slice(6, 9),
  ]

  const totalSlides = allTestimonials.length

  // Typewriter loop sur le dernier mot
  useEffect(() => {
    const word = WORDS[wordIndex]
    const typeSpeed   = 100
    const deleteSpeed = 60
    const pauseAfter  = 1800

    const timeout = setTimeout(() => {
      if (!isDeleting) {
        if (displayed.length < word.length) {
          setDisplayed(word.slice(0, displayed.length + 1))
        } else {
          setTimeout(() => setIsDeleting(true), pauseAfter)
        }
      } else {
        if (displayed.length > 0) {
          setDisplayed(word.slice(0, displayed.length - 1))
        } else {
          setIsDeleting(false)
          setWordIndex((prev) => (prev + 1) % WORDS.length)
        }
      }
    }, isDeleting ? deleteSpeed : typeSpeed)

    return () => clearTimeout(timeout)
  }, [displayed, isDeleting, wordIndex])

  // Carrousel automatique pour les témoignages

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prevSlide) => (prevSlide + 1) % totalSlides)
    }, 5000) // Change toutes les 5 secondes
    
    return () => clearInterval(interval)
  }, [totalSlides])

  return (
    <div className="min-h-screen bg-cream-50 overflow-x-hidden">

      {/* Header Accueil */}

      <header className="relative overflow-hidden w-full h-screen" style={{ backgroundColor: '#fefcf8' }}>
        {/* Image de fond */}
        <div className="absolute inset-0 w-full h-full overflow-hidden">
          <img
            src={headerImage}
            alt="Header background"
            className="w-full h-full object-cover hero-img-zoom"
            style={{ transformOrigin: 'center center' }}
          />
          {/* Overlay animé */}
          <div className="absolute inset-0 bg-black/30 hero-overlay"></div>
          {/* Gradient coloré animé */}
          <div className="absolute inset-0 hero-overlay" style={{ background: 'linear-gradient(135deg, rgba(0,42,78,0.35) 0%, transparent 50%, rgba(255,215,0,0.08) 100%)' }}></div>
        </div>

        {/* Formes flottantes décoratives */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden z-0">
          {/* Blob 1 — haut droite */}
          <div className="blob-1 absolute top-10 right-10 w-72 h-72 rounded-full opacity-10"
            style={{ background: 'radial-gradient(circle, #FFD700, transparent 70%)' }}></div>
          {/* Blob 2 — bas gauche */}
          <div className="blob-2 absolute bottom-32 left-8 w-56 h-56 rounded-full opacity-10"
            style={{ background: 'radial-gradient(circle, #002A4E, transparent 70%)' }}></div>
          {/* Blob 3 — milieu droite */}
          <div className="blob-3 absolute top-1/2 right-1/4 w-32 h-32 rounded-full opacity-8 border-2 border-white/20"></div>
          {/* Petits points lumineux */}
          <div className="blob-1 absolute top-1/3 left-1/4 w-4 h-4 rounded-full opacity-40" style={{ backgroundColor: '#FFD700', animationDelay: '2s' }}></div>
          <div className="blob-2 absolute top-1/4 right-1/3 w-3 h-3 rounded-full opacity-30" style={{ backgroundColor: '#FFD700', animationDelay: '1s' }}></div>
          <div className="blob-3 absolute bottom-1/3 right-1/4 w-2 h-2 rounded-full opacity-50" style={{ backgroundColor: 'white', animationDelay: '0.5s' }}></div>
        </div>

        {/* Contenu par-dessus l'image */}
        <div className="relative z-10 w-full h-full">

          {/* Slogan — fixé en haut */}
          <div ref={refHeroText} className="hero-text-container absolute top-8 md:top-24 left-0 right-0 pr-4 md:pr-0 pl-[calc(2vw+0.5rem)] md:pl-[calc(10vw+1rem)]">
            {/* Badge animé */}
            <div className="hero-badge-in inline-flex items-center gap-2 px-4 md:px-5 py-2 rounded-full mb-4 md:mb-5"
              style={{ backgroundColor: '#FFD700', animationDelay: '0.3s', opacity: 0 }}>
              <span className="w-2.5 h-2.5 rounded-full animate-pulse bg-white"></span>
              <span className="text-sm md:text-base font-bold" style={{ color: '#002A4E' }}>Plateforme N°1 en Côte d'Ivoire</span>
            </div>
            <p className="hero-slogan text-5xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-bold leading-tight" style={{color:'#002A4E'}}>
              Connectez vous ! <br /> Postez vos <br />
              <span style={{ color: '#FFD700' }}>{displayed}</span>
              <span className="typewriter-cursor" style={{ color: '#FFD700' }}>|</span>
            </p>
          </div>

          {/* Bloc blanc — fixé en bas */}
          <div ref={refHeroBlock} className="absolute bottom-0 left-0 right-0">

            {/* Mobile — pleine largeur sans paddingLeft */}
            <div className={`block md:hidden bg-white px-6 py-6 shadow-xl text-center anim-hidden ${inHeroBlock ? 'animate-fade-in-up' : ''}`}>
              <h1 className="text-3xl font-bold leading-none mb-2" style={{color:'#002A4E'}}>KUDHI</h1>
              <p className="text-base mb-5" style={{color:'#002A4E'}}>
                Postez vos annonces (Vidéos, Images, Bannières) pour vous rapprocher de vos cibles !
              </p>
              <Link
                to={ROUTES.REGISTER}
                className="block w-full py-5 text-center font-bold text-xl rounded-xl"
                style={{ backgroundColor: '#FFD700', color: '#001F3F' }}
              >
                Créer gratuitement un compte
              </Link>
            </div>

            {/* Desktop — avec paddingLeft aligné au texte */}
            <div
              className="hidden md:block"
              style={{ paddingLeft: 'calc(10vw + 1rem)' }}
            >
              <div className={`flex flex-row items-start justify-between gap-8 bg-white px-12 pt-5 rounded-tl-2xl shadow-xl anim-hidden ${inHeroBlock ? 'animate-fade-in-up' : ''}`} style={{ minHeight: '32vh' }}>
                <div className="flex flex-col mt-4">
                  <h1 className={`text-4xl lg:text-6xl font-bold leading-none mb-2 anim-hidden ${inHeroBlock ? 'animate-fade-in-up delay-200' : ''}`} style={{color:'#002A4E'}}>
                    KUDHI
                  </h1>
                  <p className={`text-xl lg:text-3xl leading-tight anim-hidden ${inHeroBlock ? 'animate-fade-in-up delay-300' : ''}`} style={{color:'#002A4E'}}>
                    Postez vos annonces (Vidéos, Images, Bannières) <br />
                    pour vous rapprocher de vos cibles !
                  </p>
                </div>
                <div className="flex-shrink-0 flex items-start mt-5 mr-32">
                  <Link
                    to={ROUTES.REGISTER}
                    className={`inline-block px-10 lg:px-16 py-6 lg:py-8 text-center font-bold text-2xl lg:text-4xl rounded-2xl transition-all hover:opacity-90 hover:scale-105 shadow-xl whitespace-nowrap anim-hidden ${inHeroBlock ? 'animate-fade-in-up delay-400' : ''}`}
                    style={{ backgroundColor: '#FFD700', color: '#001F3F' }}
                  >
                    Créer gratuitement <br />un compte
                  </Link>
                </div>
              </div>
            </div>

          </div>

        </div>
      </header>


      {/* Qui nous sommes */}
      <div ref={refAbout} className="relative overflow-hidden" style={{ backgroundColor: '#fefcf8' }}>
        <div className="flex flex-col lg:flex-row items-stretch min-h-[600px]">

          {/* Colonne gauche — texte */}
          <div className="w-full lg:w-1/2 flex flex-col justify-center px-8 md:px-16 py-16 relative z-10">
            {/* Cercle décoratif */}
            <div className="absolute -top-16 -left-16 w-64 h-64 rounded-full opacity-10" style={{ backgroundColor: '#FFD700' }}></div>

            <span className={`inline-flex items-center gap-2 text-sm font-semibold mb-5 w-fit px-4 py-2 rounded-full anim-hidden ${inAbout ? 'animate-fade-in-up' : ''}`} style={{ backgroundColor: '#FFD700', color: '#002A4E' }}>
              <span className="w-2 h-2 rounded-full bg-current"></span>
              Qui nous sommes ?
            </span>

            <h2 className={`text-4xl lg:text-5xl font-bold leading-tight mb-6 anim-hidden ${inAbout ? 'animate-fade-in-up delay-200' : ''}`} style={{ color: '#002A4E' }}>
              Notre équipe hautement<br />
              <span style={{ color: '#FFD700' }}>qualifiée</span> est toujours<br />
              à votre service.
            </h2>

            <div className={`space-y-4 mb-8 anim-hidden ${inAbout ? 'animate-fade-in-up delay-300' : ''}`}>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full flex-shrink-0 flex items-center justify-center mt-0.5" style={{ backgroundColor: '#FFD700' }}>
                  <svg className="w-3 h-3" fill="none" stroke="#002A4E" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
                </div>
                <p className="text-gray-600 leading-relaxed">
                  Plateforme digitale innovante de mise en relation entre clients, vendeurs et entreprises pour faciliter les échanges et transactions.
                </p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full flex-shrink-0 flex items-center justify-center mt-0.5" style={{ backgroundColor: '#FFD700' }}>
                  <svg className="w-3 h-3" fill="none" stroke="#002A4E" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
                </div>
                <p className="text-gray-600 leading-relaxed">
                  Interface intuitive permettant aux vendeurs de gérer leurs offres et aux clients de trouver rapidement ce dont ils ont besoin.
                </p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full flex-shrink-0 flex items-center justify-center mt-0.5" style={{ backgroundColor: '#FFD700' }}>
                  <svg className="w-3 h-3" fill="none" stroke="#002A4E" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
                </div>
                <p className="text-gray-600 leading-relaxed">
                  Environnement de confiance pour interagir, négocier et conclure des transactions en toute sécurité.
                </p>
              </div>
            </div>

            {/* Stats */}
            <div className={`flex gap-8 mb-8 anim-hidden ${inAbout ? 'animate-fade-in-up delay-400' : ''}`}>
              <div>
                <div className="text-3xl font-bold" style={{ color: '#002A4E' }}>500+</div>
                <div className="text-sm text-gray-500">Utilisateurs actifs</div>
              </div>
              <div className="w-px bg-gray-200"></div>
              <div>
                <div className="text-3xl font-bold" style={{ color: '#002A4E' }}>1000+</div>
                <div className="text-sm text-gray-500">Annonces publiées</div>
              </div>
              <div className="w-px bg-gray-200"></div>
              <div>
                <div className="text-3xl font-bold" style={{ color: '#002A4E' }}>98%</div>
                <div className="text-sm text-gray-500">Satisfaction</div>
              </div>
            </div>

            <Link
              to="/contact"
              className={`inline-flex items-center gap-3 px-8 py-4 rounded-xl font-bold text-lg w-fit transition-all hover:scale-105 hover:shadow-lg anim-hidden ${inAbout ? 'animate-fade-in-up delay-400' : ''}`}
              style={{ backgroundColor: '#FFD700', color: '#002A4E' }}
            >
              Contactez-nous
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" /></svg>
            </Link>
          </div>

          {/* Colonne droite — image */}
          <div className={`w-full lg:w-1/2 relative min-h-[400px] lg:min-h-0 anim-hidden ${inAbout ? 'animate-fade-in-up delay-200' : ''}`}>
            <img
              src={groupImage}
              alt="Notre équipe"
              className="w-full h-full object-cover"
              style={{ display: 'block' }}
            />
            {/* Badge flottant */}
            <div className="absolute bottom-8 left-8 bg-white rounded-2xl px-6 py-4 shadow-xl flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#FFD700' }}>
                <svg className="w-6 h-6" fill="none" stroke="#001F3F" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" /></svg>
              </div>
              <div>
                <div className="font-bold text-sm" style={{ color: '#001F3F' }}>Equipe dédiée</div>
                <div className="text-xs text-gray-500">Toujours disponible</div>
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* Fonctionnalités */}
      <div ref={refFeatures} className="py-40 px-4" style={{ backgroundColor: '#F4F5F7' }}>
        <div className="max-w-6xl mx-auto">

          {/* En-tête */}
          <div className={`text-center mb-16 anim-hidden ${inFeatures ? 'animate-fade-in-up' : ''}`}>
            <span className="inline-block px-4 py-1 rounded-full text-sm font-semibold mb-4" style={{ backgroundColor: '#FFD700', color: '#002A4E' }}>
              Nos fonctionnalités
            </span>
            <h2 className="text-4xl lg:text-5xl font-bold mb-4" style={{ color: '#002A4E' }}>
              Fonctionnalités de KUDHI
            </h2>
            <p className="text-gray-500 text-lg max-w-2xl mx-auto">
              Des outils puissants pour vous accompagner dans toutes vos activités
            </p>
          </div>

          {/* Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">

            {/* Images */}
            <div className={`group relative rounded-2xl p-8 border border-gray-100 bg-white shadow-sm hover:shadow-xl hover:-translate-y-2 transition-all duration-300 anim-hidden ${inFeatures ? 'animate-scale-in delay-100' : ''}`}>
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-6" style={{ backgroundColor: 'rgba(255,215,0,0.15)' }}>
                <svg className="w-8 h-8" fill="none" stroke="#002A4E" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold mb-3" style={{ color: '#002A4E' }}>Images</h3>
              <p className="text-gray-500 leading-relaxed text-sm">
                Publiez facilement des images de vos produits ou services. Ajoutez plusieurs photos et offrez aux clients une vue claire, détaillée et professionnelle de votre offre.
              </p>
              <div className="absolute bottom-0 left-0 right-0 h-1 rounded-b-2xl opacity-0 group-hover:opacity-100 transition-opacity" style={{ backgroundColor: '#FFD700' }}></div>
            </div>

            {/* Vidéos — carte centrale mise en avant */}
            <div className={`group relative rounded-2xl p-8 shadow-xl hover:-translate-y-2 transition-all duration-300 text-white anim-hidden ${inFeatures ? 'animate-scale-in delay-300' : ''}`} style={{ backgroundColor: '#002A4E' }}>
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-6" style={{ backgroundColor: 'rgba(255,215,0,0.15)' }}>
                <svg className="w-8 h-8" fill="none" stroke="#FFD700" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 10l4.553-2.276A1 1 0 0121 8.723v6.554a1 1 0 01-1.447.894L15 14M4 6h8a2 2 0 012 2v8a2 2 0 01-2 2H4a2 2 0 01-2-2V8a2 2 0 012-2z" />
                </svg>
              </div>
              <span className="inline-block px-3 py-1 rounded-full text-xs font-bold mb-3" style={{ backgroundColor: '#FFD700', color: '#001F3F' }}>Populaire</span>
              <h3 className="text-2xl font-bold mb-3" style={{ color: '#FFD700' }}>Vidéos</h3>
              <p className="text-gray-300 leading-relaxed text-sm">
                Offrez une expérience immersive. Ajoutez des démonstrations et présentations pour montrer vos produits en action et convaincre plus facilement.
              </p>
              <div className="absolute bottom-0 left-0 right-0 h-1 rounded-b-2xl" style={{ backgroundColor: '#FFD700' }}></div>
            </div>

            {/* Bannières */}
            <div className={`group relative rounded-2xl p-8 border border-gray-100 bg-white shadow-sm hover:shadow-xl hover:-translate-y-2 transition-all duration-300 anim-hidden ${inFeatures ? 'animate-scale-in delay-500' : ''}`}>
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-6" style={{ backgroundColor: 'rgba(255,215,0,0.15)' }}>
                <svg className="w-8 h-8" fill="none" stroke="#002A4E" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold mb-3" style={{ color: '#002A4E' }}>Bannières</h3>
              <p className="text-gray-500 leading-relaxed text-sm">
                Mettez en avant vos promotions et nouveautés avec des bannières publicitaires percutantes. Améliorez votre visibilité et renforcez votre identité visuelle.
              </p>
              <div className="absolute bottom-0 left-0 right-0 h-1 rounded-b-2xl opacity-0 group-hover:opacity-100 transition-opacity" style={{ backgroundColor: '#FFD700' }}></div>
            </div>
          </div>

          {/* Image téléphone */}
          <div className="flex justify-center pt-40" style={{ marginTop: '100px' }}>
            <img src={telImage} alt="Illustration phone" className="w-full max-w-3xl h-auto animate-float" />
          </div>

        </div>
      </div>


      {/* Temoignages */}
      <div ref={refTestim} className="py-20" style={{ backgroundColor: '#001F3F' }}>
        <div className="w-[90%] md:w-[85%] mx-auto">

          {/* En-tête */}
          <div className={`text-center mb-14 anim-hidden ${inTestim ? 'animate-fade-in-up' : ''}`}>
            <span className="inline-block px-4 py-1 rounded-full text-sm font-semibold mb-4" style={{ backgroundColor: '#FFD700', color: '#001F3F' }}>
              Témoignages
            </span>
            <h2 className="text-4xl lg:text-5xl font-bold text-white mb-4">
              Ce que disent nos utilisateurs
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              Découvrez comment KUDHI transforme le quotidien de nos clients à travers leurs expériences.
            </p>
          </div>

          {/* Carrousel */}
          <div className="relative">
            {/* Mobile : 1 carte */}
            <div className="overflow-hidden md:hidden">
              <div
                className="flex transition-transform duration-500 ease-in-out"
                style={{ transform: `translateX(-${currentSlide * 100}%)` }}
              >
                {allTestimonials.map((testimonial) => (
                  <div key={testimonial.id} className="w-full flex-shrink-0 px-2">
                    <div className="rounded-2xl p-6 h-full" style={{ backgroundColor: '#0a2d52' }}>
                      <div className="text-5xl font-serif mb-3" style={{ color: '#FFD700' }}>"</div>
                      <div className="flex gap-1 mb-4">
                        {[1,2,3,4,5].map(s => <span key={s} className="text-lg" style={{ color: '#FFD700' }}>★</span>)}
                      </div>
                      <p className="text-gray-300 text-sm leading-relaxed mb-6">
                        KUDHI a totalement changé ma façon de promouvoir mon business. En quelques jours, j'ai reçu beaucoup plus de visibilité et de contacts qualifiés.
                      </p>
                      <div className="flex items-center gap-3 pt-4 border-t border-white/10">
                        <img src={testimonial.image} alt={testimonial.name} className="w-11 h-11 rounded-full object-cover border-2 border-[#FFD700]" />
                        <div>
                          <p className="text-white font-bold text-sm">{testimonial.name}</p>
                          <p className="text-gray-400 text-xs">{testimonial.company}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Desktop : 3 cartes */}
            <div className="overflow-hidden hidden md:block">
              <div
                className="flex transition-transform duration-500 ease-in-out"
                style={{ transform: `translateX(-${Math.floor(currentSlide / 3) * 100}%)` }}
              >
                {testimonialsDesktop.map((slideGroup, slideIndex) => (
                  <div key={slideIndex} className="w-full flex-shrink-0">
                    <div className="grid grid-cols-3 gap-6">
                      {slideGroup.map((testimonial, i) => (
                        <div
                          key={testimonial.id}
                          className={`rounded-2xl p-7 flex flex-col transition-all hover:-translate-y-1 hover:shadow-2xl anim-hidden ${inTestim ? `animate-fade-in-up ${i===0?'delay-100':i===1?'delay-300':'delay-500'}` : ''}`}
                          style={{ backgroundColor: i === 1 ? '#FFD700' : '#0a2d52' }}
                        >
                          <div className="text-5xl font-serif mb-3" style={{ color: i === 1 ? '#001F3F' : '#FFD700' }}>"</div>
                          <div className="flex gap-1 mb-4">
                            {[1,2,3,4,5].map(s => (
                              <span key={s} className="text-lg" style={{ color: i === 1 ? '#001F3F' : '#FFD700' }}>★</span>
                            ))}
                          </div>
                          <p className="text-sm leading-relaxed mb-6 flex-1" style={{ color: i === 1 ? '#001F3F' : '#d1d5db' }}>
                            KUDHI a totalement changé ma façon de promouvoir mon business. En quelques jours, j'ai reçu beaucoup plus de visibilité et de contacts qualifiés grâce à la plateforme.
                          </p>
                          <div className="flex items-center gap-3 pt-4 border-t" style={{ borderColor: i === 1 ? 'rgba(0,31,63,0.2)' : 'rgba(255,255,255,0.1)' }}>
                            <img src={testimonial.image} alt={testimonial.name} className="w-12 h-12 rounded-full object-cover border-2" style={{ borderColor: i === 1 ? '#001F3F' : '#FFD700' }} />
                            <div>
                              <p className="font-bold text-sm" style={{ color: i === 1 ? '#001F3F' : '#ffffff' }}>{testimonial.name}</p>
                              <p className="text-xs" style={{ color: i === 1 ? '#001F3F99' : '#9ca3af' }}>{testimonial.company}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Dots navigation */}
            <div className="flex justify-center gap-3 mt-10 md:hidden">
              {allTestimonials.map((_, index) => (
                <button key={index} onClick={() => setCurrentSlide(index)}
                  className={`h-2 rounded-full transition-all ${index === currentSlide ? 'w-8 bg-[#FFD700]' : 'w-2 bg-white/30'}`}
                />
              ))}
            </div>
            <div className="hidden md:flex justify-center gap-3 mt-10">
              {testimonialsDesktop.map((_, index) => (
                <button key={index} onClick={() => setCurrentSlide(index * 3)}
                  className={`h-2 rounded-full transition-all ${Math.floor(currentSlide / 3) === index ? 'w-8 bg-[#FFD700]' : 'w-2 bg-white/30'}`}
                />
              ))}
            </div>
          </div>

        </div>
      </div>

      {/* Comment ça marche */}
      <div ref={refSteps} className="py-20 px-4" style={{ backgroundColor: '#fefcf8' }}>
        <div className="max-w-6xl mx-auto">

          <div className={`text-center mb-16 anim-hidden ${inSteps ? 'animate-fade-in-up' : ''}`}>
            <span className="inline-block px-4 py-1 rounded-full text-sm font-semibold mb-4" style={{ backgroundColor: '#FFD700', color: '#002A4E' }}>
              Simple &amp; Rapide
            </span>
            <h2 className="text-4xl lg:text-5xl font-bold mb-4" style={{ color: '#002A4E' }}>
              Comment ça marche ?
            </h2>
            <p className="text-gray-500 text-lg max-w-2xl mx-auto">
              Rejoignez KUDHI en 3 étapes simples et commencez à développer votre activité dès aujourd'hui.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative">
            {/* Ligne de connexion desktop */}
            <div className="hidden md:block absolute top-10 left-1/4 right-1/4 h-0.5" style={{ backgroundColor: '#FFD700', opacity: 0.4 }}></div>

            {/* Étape 1 */}
            <div className={`relative text-center anim-hidden ${inSteps ? 'animate-fade-in-up delay-100' : ''}`}>
              <div className="relative inline-flex items-center justify-center w-20 h-20 rounded-full mb-6 shadow-lg mx-auto" style={{ backgroundColor: '#FFD700' }}>
                <span className="text-3xl font-bold" style={{ color: '#002A4E' }}>1</span>
                <div className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-white border-2 flex items-center justify-center" style={{ borderColor: '#FFD700' }}>
                  <svg className="w-3 h-3" fill="none" stroke="#002A4E" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>
                </div>
              </div>
              <h3 className="text-xl font-bold mb-3" style={{ color: '#002A4E' }}>Créez votre compte</h3>
              <p className="text-gray-500 text-sm leading-relaxed">
                Inscrivez-vous gratuitement en quelques secondes. Aucune carte bancaire requise pour démarrer.
              </p>
            </div>

            {/* Étape 2 */}
            <div className={`relative text-center anim-hidden ${inSteps ? 'animate-fade-in-up delay-300' : ''}`}>
              <div className="relative inline-flex items-center justify-center w-20 h-20 rounded-full mb-6 shadow-lg mx-auto" style={{ backgroundColor: '#002A4E' }}>
                <span className="text-3xl font-bold text-white">2</span>
              </div>
              <h3 className="text-xl font-bold mb-3" style={{ color: '#002A4E' }}>Publiez vos annonces</h3>
              <p className="text-gray-500 text-sm leading-relaxed">
                Ajoutez vos images, vidéos et bannières en quelques clics. Décrivez votre offre et fixez vos tarifs.
              </p>
            </div>

            {/* Étape 3 */}
            <div className={`relative text-center anim-hidden ${inSteps ? 'animate-fade-in-up delay-500' : ''}`}>
              <div className="relative inline-flex items-center justify-center w-20 h-20 rounded-full mb-6 shadow-lg mx-auto" style={{ backgroundColor: '#FFD700' }}>
                <span className="text-3xl font-bold" style={{ color: '#002A4E' }}>3</span>
                <div className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-white border-2 flex items-center justify-center" style={{ borderColor: '#FFD700' }}>
                  <svg className="w-3 h-3" fill="none" stroke="#002A4E" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>
                </div>
              </div>
              <h3 className="text-xl font-bold mb-3" style={{ color: '#002A4E' }}>Développez votre activité</h3>
              <p className="text-gray-500 text-sm leading-relaxed">
                Atteignez des milliers de clients, recevez des contacts et boostez votre chiffre d'affaires.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Final */}
      <div ref={refCta} className="py-24 px-4 relative overflow-hidden" style={{ backgroundColor: '#002A4E' }}>
        {/* Cercles décoratifs */}
        <div className="absolute top-0 right-0 w-96 h-96 rounded-full opacity-10 translate-x-1/2 -translate-y-1/2" style={{ backgroundColor: '#FFD700' }}></div>
        <div className="absolute bottom-0 left-0 w-64 h-64 rounded-full opacity-10 -translate-x-1/2 translate-y-1/2" style={{ backgroundColor: '#FFD700' }}></div>

        <div className={`max-w-4xl mx-auto text-center relative z-10 anim-hidden ${inCta ? 'animate-scale-in' : ''}`}>
          <span className="inline-block px-4 py-1 rounded-full text-sm font-semibold mb-6" style={{ backgroundColor: 'rgba(255,215,0,0.15)', color: '#FFD700' }}>
            Rejoignez-nous
          </span>
          <h2 className="text-4xl lg:text-6xl font-bold text-white mb-6 leading-tight">
            Prêt à booster votre<br />
            <span style={{ color: '#FFD700' }}>visibilité</span> avec KUDHI ?
          </h2>
          <p className="text-gray-400 text-lg mb-10 max-w-2xl mx-auto">
            Rejoignez des centaines d'entrepreneurs qui font confiance à KUDHI pour développer leur activité et atteindre leurs cibles.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to={ROUTES.REGISTER}
              className="inline-flex items-center justify-center gap-3 px-10 py-5 rounded-xl font-bold text-xl transition-all hover:scale-105 hover:shadow-2xl"
              style={{ backgroundColor: '#FFD700', color: '#002A4E' }}
            >
              Créer mon compte gratuitement
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" /></svg>
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-3 px-10 py-5 rounded-xl font-bold text-xl border-2 transition-all hover:scale-105 text-white"
              style={{ borderColor: 'rgba(255,215,0,0.5)' }}
            >
              Nous contacter
            </Link>
          </div>
          {/* Badges de confiance */}
          <div className="flex flex-wrap items-center justify-center gap-6 mt-12">
            <div className="flex items-center gap-2 text-gray-400 text-sm">
              <svg className="w-5 h-5" style={{ color: '#FFD700' }} fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></svg>
              Inscription gratuite
            </div>
            <div className="flex items-center gap-2 text-gray-400 text-sm">
              <svg className="w-5 h-5" style={{ color: '#FFD700' }} fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></svg>
              Sans engagement
            </div>
            <div className="flex items-center gap-2 text-gray-400 text-sm">
              <svg className="w-5 h-5" style={{ color: '#FFD700' }} fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></svg>
              Support 7j/7
            </div>
          </div>
        </div>
      </div>

    </div>
  )
}

export default HomePage
